import { type Content, type ContentHref, ContentType } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { selectDocumentRootItem } from '@newStore/documentApi/documentApiSelectors';
import { expandLlinkidGoalRelations } from '@newStore/llinkid/llinkidActions';
import { selectPreviousVersionHrefs } from '@newStore/llinkid/llinkidSelectors';
import type { EditAsidePreviousVersionItems } from '@nodeTypeConfig/configTypes';
import { getResourceKey } from '@store/helpers/documentHelpers';

import type React from 'react';
import { any } from 'prop-types';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as DOCUMENT_ACTIONS from '../../../../reduxLoop/actions/documentActions';
import { useEditHref } from '../../../hooks/UseEditHref';
import AsideLlinkidGoal from '../asideLlinkidGoal/asideLlinkidGoal';
import AsideLlinkidItem from '../asideLlinkidItem/asideLlinkidItem';
import AsideRelationsPanel from '../asideRelationsPanel/AsideRelationsPanel';
import { useDeleteRelationHook } from '../asideRelationsPanel/useDeleteRelationHook';
import { selectReplacesRelations } from './AsidePreviousVersionItemsSelectors';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import LlinkidPreviousVersionModal from '@UI/modals/LlinkidPreviousVersionModal';
import LlinkidGoalRelationsModal from '@UI/modals/LlinkidGoalRelationsModal';

const AsidePreviousVersionItems: React.FC<{
  config: EditAsidePreviousVersionItems;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();

  const currentEditingHref = useEditHref();

  const rootItem = useSelector(selectDocumentRootItem) as Content;
  const llinkidPreviousVersionModal = useRef<Modal>();
  const llinkidGoalRelationsModal = useRef<Modal>();

  const [previousCurriculumHref] = useSelector((state: RootState) =>
    selectPreviousVersionHrefs(state, rootItem.$$meta.permalink),
  );

  const multipleRelationTypes = config.options.relationTypes.length > 1;
  const isReadonly = readonly || !previousCurriculumHref || rootItem.version?.major === 1;

  const replacesRelations = useSelector((state: RootState) =>
    selectReplacesRelations(state, config),
  );

  useEffect(() => {
    dispatch(
      expandLlinkidGoalRelations({
        key: getResourceKey(currentEditingHref),
        relationTypes: config.options.relationTypes,
        originPart: 'from',
      }),
    );
  }, [dispatch, currentEditingHref, config.options.relationTypes]);

  const currentModal = config.options.modal === 'llinkidPreviousVersionModal'
    ? llinkidPreviousVersionModal : llinkidGoalRelationsModal;
  const onDelete = useDeleteRelationHook();

  const onCreate = async () => {
    const selectedValues = replacesRelations.map((rel) => getResourceKey(rel.to?.href));
    currentModal.current?.open({
      selectedValues,
      defaultCurriculumHref: previousCurriculumHref,
      multipleRelationTypes,
      onSubmit: (response) => {
        if (response) {
          let newRelation;
          if (config.options.modal === 'llinkidPreviousVersionModal') {
            newRelation = {
              relationtype: config.options.relationTypes[0],
              from: {
                href: currentEditingHref,
              },
              to: {
                href: response.previousVersionHref,
              },
            };
          } else if (config.options.modal === 'llinkidGoalRelationsModal') {
            newRelation = {
              relationtype: config.options.relationTypes[0],
              from: {
                href: currentEditingHref,
              },
              to: {
                href: response.goalHref,
              },
            };
          }

          if (newRelation) {
            const relationsParams = {
              key: getResourceKey(currentEditingHref),
              relationTypes: config.options.relationTypes,
            };

            // @ts-expect-error addRelationAction is not typed
            dispatch(DOCUMENT_ACTIONS.addRelationAction(newRelation, true, true, relationsParams));
          }
        }
      },
    });
  };

  return (
    <>
      <AsideRelationsPanel
        title={config.label}
        items={replacesRelations}
        readOnly={isReadonly}
        onCreate={onCreate}
        onDelete={onDelete}
      >
        {config.options.type !== ContentType.LLINKID_GOAL
          ? (item) => <AsideLlinkidItem href={item.to.href} />
          : (item) => (
              <AsideLlinkidGoal
                item={item}
                options={config.options}
                href={item.to.href as ContentHref}
              />
            )}
      </AsideRelationsPanel>
      <Modal
        ref={llinkidGoalRelationsModal}
        size="sm"
        component={<LlinkidGoalRelationsModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
      <Modal
        ref={llinkidPreviousVersionModal}
        size="sm"
        component={<LlinkidPreviousVersionModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsidePreviousVersionItems.displayName = 'AsidePreviousVersionItems';

AsidePreviousVersionItems.propTypes = {
  config: any,
  readonly: any,
};

export default AsidePreviousVersionItems;
